import { Badge } from '@/components/ui/badge';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useFailedSubmissionsStatistics } from '@/hooks/use-failed-submissions';
import { useAuth } from '@/contexts/auth-context';
import {
  canManageContent as hasContentAccess,
  canManageRegistrations as hasRegistrationsAccess,
  isAdministrator,
} from '@/lib/role-access';
import {
  AlertCircle,
  BriefcaseBusiness,
  FolderKanban,
  Import,
  LogOut,
  Newspaper,
  Plug,
  ScrollText,
  Settings,
  Users,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const registrationNavigationItems = [
  {
    title: 'Projecten',
    url: '/projecten',
    icon: FolderKanban,
  },
  {
    title: 'Groepen',
    url: '/groepen',
    icon: Users,
  },
  {
    title: 'Formulier Providers',
    url: '/formulier-providers',
    icon: Plug,
  },
  {
    title: 'Mislukte Inzendingen',
    url: '/mislukte-inzendingen',
    icon: AlertCircle,
  },
  {
    title: 'Instellingen',
    url: '/registraties/instellingen',
    icon: Settings,
  },
];
const websiteNavigationItems = [
  {
    title: 'Cases',
    url: '/cases',
    icon: BriefcaseBusiness,
  },
  { title: 'Blog', url: '/blog', icon: Newspaper },
  { title: 'Migratie', url: '/migration', icon: Import },
  { title: 'Instellingen', url: '/website/instellingen', icon: Settings },
];

export function AppSidebar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isAdmin = isAdministrator(user?.roles ?? []);
  const canManageRegistrations = hasRegistrationsAccess(user?.roles ?? []);
  const canManageContent = hasContentAccess(user?.roles ?? []);
  const appVersion = import.meta.env.VITE_APP_VERSION;
  const { data: failedSubmissionStats } = useFailedSubmissionsStatistics();
  const pendingFailedSubmissionsCount =
    failedSubmissionStats?.filter(stat => stat.status === 'pending').reduce((sum, stat) => sum + stat.count, 0) ?? 0;
  const failedSubmissionsBadgeLabel =
    pendingFailedSubmissionsCount > 99 ? '99+' : pendingFailedSubmissionsCount.toString();

  return (
    <Sidebar>
      <SidebarHeader className="border-b px-4 py-4 group-data-[collapsible=icon]:px-2">
        <Link
          to="/"
          className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0"
        >
          <img src="/FAME-logo.webp" alt="FAME" className="hidden h-8 w-auto group-data-[state=expanded]:block" />
          <img src="/FAME-star.svg" alt="FAME" className="block h-6 w-6 shrink-0 group-data-[state=expanded]:hidden" />
          <span className="hidden items-baseline gap-1 group-data-[state=expanded]:inline-flex">
            <span className="font-semibold text-lg">Backoffice</span>
            <span className="text-xs text-muted-foreground">v{appVersion}</span>
          </span>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {canManageRegistrations && (
          <SidebarGroup>
            <SidebarGroupLabel>Registraties</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {registrationNavigationItems.map(item => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={location.pathname.startsWith(item.url)}>
                      <Link to={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                        {item.url === '/mislukte-inzendingen' && pendingFailedSubmissionsCount > 0 && (
                          <Badge variant="destructive" className="ml-auto h-5 min-w-5 px-1.5 text-xs">
                            {failedSubmissionsBadgeLabel}
                          </Badge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {canManageContent && (
          <SidebarGroup>
            <SidebarGroupLabel>Website</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {websiteNavigationItems.map(item => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={location.pathname.startsWith(item.url)}>
                      <Link to={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t">
        <SidebarMenu>
          {isAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={location.pathname.startsWith('/gebruikers')}>
                <Link to="/gebruikers">
                  <Users />
                  <span>Gebruikers</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {isAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={location.pathname.startsWith('/instellingen')}>
                <Link to="/instellingen">
                  <Settings />
                  <span>Instellingen</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {isAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={location.pathname.startsWith('/logs')}>
                <Link to="/logs">
                  <ScrollText />
                  <span>Logs</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => void logout()}>
              <LogOut />
              <span>Afmelden</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
